import app from 'flarum/forum/app';
import COUNTRIES from '../../common/countries';

/** The business directory on the website: shared by the list, a business's page, the form and the review queue. */
export const LIST_ROUTE = 'voucha-community.businesses';
export const NEW_ROUTE = 'voucha-community.businesses.new';
export const REVIEW_ROUTE = 'voucha-community.businesses.review';
export const PAGE_ROUTE = 'voucha-community.business';
export const JOIN_ROUTE = 'voucha-community.businesses.join';
export const api = path => app.forum.attribute('apiUrl') + '/voucha-community' + path;
function routeName() {
  try {
    return app.current && app.current.get('routeName') || '';
  } catch (e) {
    return '';
  }
}
const path = () => {
  try {
    return (m.route.get() || '').split('?')[0];
  } catch (e) {
    return '';
  }
};
export const onList = () => routeName() === LIST_ROUTE || path() === '/businesses';
export const onNew = () => routeName() === NEW_ROUTE || path() === '/businesses/new';
export const onReview = () => routeName() === REVIEW_ROUTE || path() === '/businesses/review';
export const onPage = () => routeName() === PAGE_ROUTE || /^\/businesses\/[a-z0-9-]+$/.test(path()) && !onNew() && !onReview();
export const onJoin = () => routeName() === JOIN_ROUTE || /^\/businesses\/join\/[a-f0-9]+$/.test(path());
export const onAnyBusinesses = () => onList() || onNew() || onReview() || onPage() || onJoin();
/** /businesses/{slug}?edit=1 shows the form for that business */
export const editing = () => onPage() && m.route.param('edit') === '1';

/** The categories (cached for the visit) */
let cats = null;
let cityLists = {};
let loadingCats = false;
export function categories() {
  if (!cats && !loadingCats) {
    loadingCats = true;
    app.request({
      method: 'GET',
      url: api('/business-categories'),
      errorHandler: () => {}
    }).then(r => {
      cats = r && r.categories || [];
      cityLists = r && r.cities || {};
    }).catch(() => cats = []).then(() => m.redraw());
  }
  return cats || [];
}

/** A country's city tags (Canada, the UK, the US): the cities a business there can be linked to */
export function cityTags(code) {
  categories();
  return cityLists && cityLists[code] || [];
}

/** Nigeria's states (cached) */
let states = null;
export function ngStates() {
  if (!states) {
    states = [];
    app.request({
      method: 'GET',
      url: api('/states'),
      errorHandler: () => {}
    }).then(r => states = r && r.states || []).then(() => m.redraw()).catch(() => {});
  }
  return states;
}
export function go(url) {
  return ev => {
    if (ev && (ev.ctrlKey || ev.metaKey || ev.shiftKey)) return;
    if (ev) ev.preventDefault();
    m.route.set(url);
  };
}
export const statusLabel = {
  operating: 'Operating',
  paused: 'Paused',
  closed: 'Closed'
};

/** The verification line: what was checked, never the numbers */
export function verifiedText(v) {
  if (!v || v.status !== 'verified') return null;
  return 'Verified: ' + (v.kind || 'registration');
}
export const VOUCH_NOTE = 'A vouch is a member’s personal endorsement. It isn’t proof of registration, professional accreditation or service quality.';
export const GAPS_NOTE = 'Counts are businesses listed on Voucha by its members. A category with none listed is a potential gap, not proof that the place has none.';
export function initials(name) {
  return String(name || '?').split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}
export function logo(b, size) {
  // size 0: fills its box (the big square on a business's page and in the form)
  const fill = !size;
  const s = size || 48;
  const box = fill ? {} : {
    width: s + 'px',
    height: s + 'px'
  };
  return b.logoUrl ? m('img.VBiz-logo' + (fill ? '.VBiz-logo--fill' : ''), {
    src: b.logoUrl,
    alt: '',
    style: box
  }) : m('span.VBiz-logo.VBiz-logo--initials' + (fill ? '.VBiz-logo--fill' : ''), {
    style: fill ? {} : Object.assign(box, {
      fontSize: Math.round(s / 2.6) + 'px'
    })
  }, initials(b.name));
}

/**
 * After "New discussion" on a business's page: the discussion the member posts next (within 30 minutes) becomes
 * the business's update. Called when a discussion page shows a discussion.
 */
export function linkNewDiscussion(discussion) {
  let p = null;
  try {
    p = JSON.parse(sessionStorage.getItem('vbizPending') || 'null');
  } catch (e) {}
  if (!p) return;
  const me = app.session.user;
  const created = discussion.createdAt && discussion.createdAt();
  if (!me || Date.now() - p.at > 30 * 60e3) {
    try {
      sessionStorage.removeItem('vbizPending');
    } catch (e) {}
    return;
  }
  // only a discussion of theirs started after they pressed the button
  if (!discussion.user() || discussion.user() !== me || !created || created.getTime() < p.at - 60e3) return;
  try {
    sessionStorage.removeItem('vbizPending');
  } catch (e) {}
  app.request({
    method: 'POST',
    url: api('/businesses/' + p.id + '/discussions'),
    body: {
      discussionId: Number(discussion.id())
    },
    errorHandler: () => {}
  }).then(() => app.alerts.show({
    type: 'success'
  }, 'Posted as an update from ' + p.name + '.')).catch(() => {});
}

/** Where businesses can be listed: Nigeria, then the 15 countries with the most Nigerians abroad (by name) */
const BIZ_CODES = ['US', 'GB', 'CA', 'IE', 'DE', 'IT', 'ES', 'NL', 'FR', 'ZA', 'GH', 'AE', 'SA', 'CN', 'AU'];
export function bizCountries(keep) {
  const name = c => (COUNTRIES.find(x => x[0] === c) || [c, c])[1];
  const rest = BIZ_CODES.map(c => [c, name(c)]).sort((a, b) => a[1].localeCompare(b[1]));
  const out = [['NG', name('NG')]].concat(rest);
  // a business saved before the list was narrowed keeps its country
  if (keep && !out.some(x => x[0] === keep)) out.push([keep, name(keep)]);
  return out;
}
flarum.reg.add('voucha-community', 'forum/businesses/util', { LIST_ROUTE: LIST_ROUTE,NEW_ROUTE: NEW_ROUTE,REVIEW_ROUTE: REVIEW_ROUTE,PAGE_ROUTE: PAGE_ROUTE,JOIN_ROUTE: JOIN_ROUTE,api: api,onList: onList,onNew: onNew,onReview: onReview,onPage: onPage,onJoin: onJoin,onAnyBusinesses: onAnyBusinesses,editing: editing,categories: categories,cityTags: cityTags,ngStates: ngStates,go: go,statusLabel: statusLabel,verifiedText: verifiedText,VOUCH_NOTE: VOUCH_NOTE,GAPS_NOTE: GAPS_NOTE,initials: initials,logo: logo,linkNewDiscussion: linkNewDiscussion,bizCountries: bizCountries, });